import React, { useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { CustomPicker } from '../../components/common/CustomPicker';
import { submitIntro } from '../../lib/api/referrals';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';

type SubmitIntroNavigationProp = StackNavigationProp<ClubsStackParamList, 'SubmitIntro'>;
type SubmitIntroRouteProp = RouteProp<ClubsStackParamList, 'SubmitIntro'>;

const FAMILIARITY_OPTIONS = [
  { label: 'Close Friend/Family', value: 'close' },
  { label: 'Good Friend', value: 'good_friend' },
  { label: 'Professional Colleague', value: 'colleague' },
  { label: 'Business Partner', value: 'business_partner' },
  { label: 'Acquaintance', value: 'acquaintance' },
  { label: 'Met Once or Twice', value: 'met_once' },
];

const SubmitIntroScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<SubmitIntroNavigationProp>();
  const route = useRoute<SubmitIntroRouteProp>();
  const { requestId } = route.params;
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactDetails, setContactDetails] = useState('');
  const [familiarity, setFamiliarity] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { keyboardHeight } = useKeyboardHeight();

  const handleSubmit = async () => {
    if (!contactName.trim()) {
      showErrorToast('Please enter contact name');
      return;
    }
    if (!contactPhone.trim()) {
      showErrorToast('Please enter contact phone number');
      return;
    }

    const phoneDigits = contactPhone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      showErrorToast('Phone number must be exactly 10 digits');
      return;
    }

    if (!contactDetails.trim()) {
      showErrorToast('Please provide details about the contact');
      return;
    }

    try {
      setSubmitting(true);

      await submitIntro(requestId, {
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactDetails: contactDetails.trim(),
        familiarity,
      });

      showSuccessToast('Lead submitted successfully!');
      navigation.goBack();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to submit intro');
    } finally {
      setSubmitting(false);
    }
  };

  const phoneDigits = contactPhone.replace(/\D/g, '');
  const isFormValid = Boolean(contactName.trim()) && phoneDigits.length === 10 && Boolean(contactDetails.trim());

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View
        style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          Submit Lead
        </ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.formGroup}>
          <View style={styles.labelRow}>
            <Ionicons name="person-outline" size={18} color={theme.accent} />
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Contact Name *
            </ThemedText>
          </View>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
            placeholder="Full name of the contact"
            placeholderTextColor={theme.textTertiary}
            value={contactName}
            onChangeText={setContactName}
            maxLength={100}
          />
          <ThemedText style={[styles.hint, { color: theme.textTertiary }]}>
            Who are you introducing?
          </ThemedText>
        </View>

        <View style={styles.formGroup}>
          <View style={styles.labelRow}>
            <Ionicons name="call-outline" size={18} color={theme.accent} />
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Contact Phone Number *
            </ThemedText>
          </View>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
            placeholder="+1 234 567 8900"
            placeholderTextColor={theme.textTertiary}
            value={contactPhone}
            onChangeText={setContactPhone}
            keyboardType="phone-pad"
            maxLength={10}
          />
          <ThemedText style={[styles.hint, { color: theme.textTertiary }]}>
            Best number to reach them
          </ThemedText>
        </View>

        <View style={styles.formGroup}>
          <View style={styles.labelRow}>
            <Ionicons name="people-outline" size={18} color={theme.accent} />
            <ThemedText style={[styles.label, { color: theme.text }]}>
              How Well Do You Know Them?
            </ThemedText>
          </View>
          <CustomPicker
            selectedValue={familiarity}
            onValueChange={setFamiliarity}
            items={FAMILIARITY_OPTIONS}
            placeholder="Select familiarity level"
          />
          <ThemedText style={[styles.hint, { color: theme.textTertiary }]}>
            This helps assess the quality of the introduction
          </ThemedText>
        </View>

        <View style={styles.formGroup}>
          <View style={styles.labelRow}>
            <Ionicons name="document-text-outline" size={18} color={theme.accent} />
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Why This Person? *
            </ThemedText>
          </View>
          <TextInput
            style={[
              styles.input,
              styles.textArea,
              { backgroundColor: theme.card, borderColor: theme.border, color: theme.text },
            ]}
            placeholder="Explain why this person is a great match. Include their background, expertise, and how they can help..."
            placeholderTextColor={theme.textTertiary}
            value={contactDetails}
            onChangeText={setContactDetails}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
            maxLength={500}
          />
          <ThemedText style={[styles.hint, { color: theme.textTertiary }]}>
            {contactDetails.length}/500 characters
          </ThemedText>
        </View>

        <View style={[styles.exampleBox, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <ThemedText style={[styles.exampleTitle, { color: theme.text }]}>
            Example:
          </ThemedText>
          <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
            <ThemedText style={{ fontWeight: '600' }}>Name:</ThemedText> John Smith
          </ThemedText>
          <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
            <ThemedText style={{ fontWeight: '600' }}>Phone:</ThemedText> +1 555 123 4567
          </ThemedText>
          <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
            <ThemedText style={{ fontWeight: '600' }}>Why:</ThemedText> John owns 3 successful restaurants in downtown and is always looking for better marketing solutions. He's been in the hospitality business for 15 years and has a great network of other restaurant owners.
          </ThemedText>
        </View>

        <View style={[styles.guideBox, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <ThemedText style={[styles.guideTitle, { color: theme.text }]}>
            📝 Guidelines
          </ThemedText>
          <View style={styles.guideItem}>
            <Ionicons name="checkmark-circle" size={16} color="#10B981" />
            <ThemedText style={[styles.guideText, { color: theme.textSecondary }]}>
              Ensure you have permission to share their contact.
            </ThemedText>
          </View>
          <View style={styles.guideItem}>
            <Ionicons name="checkmark-circle" size={16} color="#10B981" />
            <ThemedText style={[styles.guideText, { color: theme.textSecondary }]}>
              Provide accurate and helpful information in the introduction.
            </ThemedText>
          </View>
        </View>

        <TouchableOpacity
          style={[
            styles.submitButton,
            { backgroundColor: isFormValid ? theme.accent : theme.border },
          ]}
          onPress={handleSubmit}
          disabled={!isFormValid || submitting}
          activeOpacity={0.7}
        >
          {submitting ? (
            <ActivityIndicator color={theme.textInverse} />
          ) : (
            <>
              <Ionicons name="send" size={20} color={isFormValid ? theme.textInverse : theme.textTertiary} />
              <ThemedText
                style={[
                  styles.submitButtonText,
                  { color: isFormValid ? theme.textInverse : theme.textTertiary },
                ]}
              >
                Submit Lead
              </ThemedText>
            </>
          )}
        </TouchableOpacity>

        <FormFooterSpacer
          headerHeight={headerHeight}
          bottomInset={bottomInset}
          keyboardHeight={keyboardHeight}
        />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingTop: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
  },
  textArea: {
    minHeight: 140,
    paddingTop: 12,
  },
  hint: {
    fontSize: 12,
    marginTop: 6,
  },
  exampleBox: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
  },
  exampleTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  exampleText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 6,
  },
  guideBox: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 16,
    marginBottom: 24,
  },
  guideTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 12,
  },
  guideItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  guideText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 10,
    gap: 8,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default SubmitIntroScreen;
